import { PAYMENT_SERVER_URL } from '../config';
import { paymentServerFetch } from './paymentServer';
import { supabase } from './supabase';

/**
 * Planes a medida (ADR-662): planes negociados uno por cliente, cobrados por Stripe.
 *
 * El link de pago es `/alta-empresa/<slug>`, el mismo componente que usan los planes Ultra.
 * La diferencia es de dónde salen los datos: en vez del catálogo hardcodeado de
 * `checkoutPlans.ts`, se le piden al payment server, que a su vez lee el NOMBRE y la CUOTA de
 * la tabla `planes_custom` y el PRECIO del Price de Stripe en vivo.
 *
 * Ese detalle es el punto de todo el diseño: el número que ve el cliente en la landing es,
 * por construcción, el que Stripe le va a cobrar. No hay dos fuentes que puedan divergir.
 */
export interface PlanCustomPublico {
  slug: string;
  nombre: string;
  maxFotosMes: number;
  /** Unidad mínima de la moneda (centavos en usd), tal como lo devuelve Stripe. */
  precio: number;
  /** ISO en minúsculas ('usd'). */
  moneda: string;
  /** 'month' | 'year' | null. */
  intervalo: string | null;
}

export class PlanCustomNoDisponible extends Error {
  constructor(
    message: string,
    /** `not_found` = el link no corresponde a ningún plan. `temporal` = reintentar sirve. */
    readonly tipo: 'not_found' | 'temporal',
  ) {
    super(message);
    this.name = 'PlanCustomNoDisponible';
  }
}

/**
 * Trae el plan a medida de un slug. Distingue los dos casos que la UI tiene que tratar
 * distinto: un link que no existe (mensaje definitivo) vs. Stripe/el server caídos (invitar a
 * reintentar). Confundirlos le diría a un cliente con un link válido que su plan no existe.
 */
export async function fetchPlanCustom(slug: string): Promise<PlanCustomPublico> {
  let resp: Response;
  try {
    resp = await paymentServerFetch(
      `${PAYMENT_SERVER_URL}/stripe/plan-custom/${encodeURIComponent(slug)}`,
      { method: 'GET' },
    );
  } catch (err) {
    throw new PlanCustomNoDisponible(`Red caída al pedir el plan: ${String(err)}`, 'temporal');
  }

  if (resp.status === 404) {
    throw new PlanCustomNoDisponible(`Plan inexistente o inactivo: ${slug}`, 'not_found');
  }
  if (!resp.ok) {
    throw new PlanCustomNoDisponible(`El server respondió ${resp.status}`, 'temporal');
  }

  const data = await resp.json();
  if (!data?.plan) {
    throw new PlanCustomNoDisponible('Respuesta sin plan', 'temporal');
  }
  return data.plan as PlanCustomPublico;
}

/** Plan a medida de la empresa logueada, para mostrarlo en la app (no en la landing). */
export interface MiPlanCustom {
  nombre: string;
  /** Cuota acordada. `null` si la fila no se pudo leer — mostrar el dato de la empresa. */
  maxFotosMes: number | null;
}

/** Label de respaldo cuando hay plan a medida pero no se pudo leer su nombre. */
const NOMBRE_FALLBACK = 'Plan Empresa';

/**
 * Datos de presentación del plan a medida de una empresa.
 *
 * IMPORTANTE — quién decide qué: **el que decide si la empresa tiene plan a medida es
 * `company_profiles.plan_custom_id`, no esta función.** Los callers ya leen esa columna en su
 * propia consulta de la empresa y solo llaman acá para el nombre y la cuota.
 *
 * Esa separación es de seguridad, no de estilo. Antes esta función resolvía las dos cosas y
 * devolvía `null` ante cualquier fallo, con lo que un error transitorio de Supabase degradaba a
 * un cliente a medida en "plan estándar": el Paywall le mostraba el catálogo público y, al
 * clickear un plan, `handleSubscription` lo mandaba al Billing Portal de Stripe — justo el
 * camino de cambio de plan que ADR-664 cerró. Fallaba ABIERTO en un guard comercial.
 *
 * Ahora falla CERRADO: si hay `plan_custom_id` pero la fila no se puede leer, se devuelven
 * igual datos de plan a medida (con nombre genérico), así el gating se mantiene y lo único que
 * se degrada es el texto en pantalla.
 *
 * Lee `planes_custom` directo por Supabase: la RLS solo deja ver LA fila de la propia empresa
 * (migración 002), así que no hace falta endpoint ni denormalizar el nombre.
 */
export async function fetchDatosPlanCustom(planCustomId: string): Promise<MiPlanCustom> {
  try {
    const { data, error } = await supabase
      .from('planes_custom')
      .select('nombre, max_fotos_mes')
      .eq('id', planCustomId)
      .maybeSingle();
    if (error || !data) return { nombre: NOMBRE_FALLBACK, maxFotosMes: null };
    return {
      nombre: (data as { nombre: string }).nombre || NOMBRE_FALLBACK,
      maxFotosMes: (data as { max_fotos_mes: number }).max_fotos_mes ?? null,
    };
  } catch {
    return { nombre: NOMBRE_FALLBACK, maxFotosMes: null };
  }
}

/**
 * Nombre del plan para copys que YA dicen "plan" ("Suscribite al Plan …", "tu plan …").
 *
 * Los planes del catálogo se llaman "Básico", "Pro", "Ultra Nivel 1" — sin la palabra "Plan",
 * que la pone el copy. Un plan a medida lo nombramos nosotros y es natural escribirle
 * "Plan de Prueba", con lo que salía "Suscribite al Plan Plan de Prueba".
 *
 * Se resuelve acá y no con una convención de nombres a propósito: una regla que hay que
 * recordar cada vez que se carga un plan se rompe sola. Así el nombre se puede escribir como
 * salga, con o sin "Plan" adelante, y la pantalla se lee bien igual.
 */
export function nombrePlanSinPrefijo(nombre: string): string {
  return nombre.replace(/^\s*plan\s+/i, '').trim() || nombre;
}

/**
 * Features que se muestran en la landing de un plan a medida.
 *
 * Arrancó derivándose de las de Ultra (un plan a medida es Ultra en capacidades), pero la lista
 * terminó divergiendo lo suficiente como para que derivarla salga más caro que escribirla:
 *
 *  · Sin las líneas de conteo de Ultra ("Hasta N imágenes/videos por mes"). Acá la cuota es la
 *    negociada y las imágenes y los videos salen del MISMO pozo de créditos, así que dos
 *    números separados no aplican — por eso el primer ítem es la cuota en créditos.
 *  · Sin "Mejor relación precio / imagen" ni "Asesoría estratégica para campañas".
 *  · "Creación y edición de imágenes con IA" (Ultra dice "Función de Edición de imágenes con
 *    IA") y va segunda, porque es la capacidad principal del producto.
 *
 * Es copy propio de estos planes: si cambia el de los planes públicos, este NO cambia solo.
 */
export function featuresPlanCustom(maxFotosMes: number): string[] {
  return [
    `${maxFotosMes.toLocaleString('es-AR')} créditos por mes`,
    'Creación y edición de imágenes con IA',
    'Estudio de Video para editar y crear videos',
    'Acceso desde computadora, tablet y celular',
    'Soporte dedicado 24/7',
  ];
}

/**
 * Formatea el precio de un plan a medida. No pasa por `formatPriceForPais`: ese módulo convierte
 * un precio en ARS a la moneda del país, y acá el precio YA viene en su moneda final desde
 * Stripe. Meterlo por esa conversión lo mostraría mal.
 */
export function formatPrecioCustom(precio: number, moneda: string): string {
  // Stripe da el monto en la unidad mínima. Las monedas de cero decimales (JPY, CLP, …) no se
  // dividen; el resto sí. Se listan las que pueden aparecer acá.
  const SIN_DECIMALES = new Set(['jpy', 'clp', 'krw', 'vnd', 'pyg', 'isk']);
  const iso = (moneda || 'usd').toLowerCase();
  const monto = SIN_DECIMALES.has(iso) ? precio : precio / 100;
  try {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: iso.toUpperCase(),
      minimumFractionDigits: 0,
      maximumFractionDigits: monto % 1 === 0 ? 0 : 2,
    }).format(monto);
  } catch {
    // Moneda que Intl no conoce → formato plano antes que romper la pantalla.
    return `${iso.toUpperCase()} ${monto}`;
  }
}
